import * as React from 'react';

// Material-UI
import Box from '@mui/material/Box';
import BlockIcon from '@mui/icons-material/Block';
import { blueGrey, green, red } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridOverlay,
  GridRowSelectionModel
} from '@mui/x-data-grid';
import PendingIcon from '@mui/icons-material/Pending';
import Tooltip from '@mui/material/Tooltip';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  TransferablePass
} from '../../models';

import {
  NULL_DATETIME_STRING,
  PassStatusID
} from '../../global';

// UTC to local date formatting hooks
import { useDates } from '../../hooks/UseDates';

// #endregion

// #region interfaces

interface PassesListGridProps {
  isLoading: boolean;
  // Change 'string' to the specific allowed literals
  handlePassesSelected: (passesSelected: { type: 'include' | 'exclude'; ids: Set<number> }) => void;
  // Change this to match your new state shape
  passesSelected: {
    type: string;
    ids: Set<number>;
  };
  rows: Array<TransferablePass>;
}

// #endregion

const PassesListGrid: React.FC<PassesListGridProps> = (props) => {

  const { dateStringToLocalDateFormatted } = useDates();

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      flexGrow: 1,
      height: '100%',
    },
    confirmed: {
      color: green[500]
    },
    deined: {
      color: red[500]
    },
    noRows: {
      margin: theme.spacing(2, 0, 0, 0),
      padding: theme.spacing(2, 0, 2, 0),
    },
    pending: {
      color: blueGrey[500]
    }
  };

  // #endregion

  // #region Methods

  /*
* Grid column definitions
*/
  const columns = (): Array<GridColDef> => {

    return [
      {
        field: 'passDate',
        headerName: 'Pass Date',
        width: 100,
        type: 'string',
        align: 'left',
        renderCell: (params: GridCellParams) => (
          renderPassDate(params)
        )
      },
      {
        field: 'fullName',
        headerName: 'Member',
        width: 175,
        type: 'string',
        align: 'left',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 75,
        type: 'string',
        align: 'center',
        renderCell: (params: GridCellParams) => (
          renderStatus(params)
        )
      },
      {
        field: 'requested',
        headerName: 'Requested',
        width: 150,
        type: 'string',
        align: 'left',
        renderCell: (params: GridCellParams) => (
          renderDate(params)
        )
      },
      {
        field: 'updated',
        headerName: 'Updated',
        width: 150,
        type: 'string',
        align: 'left',
        renderCell: (params: GridCellParams) => (
          renderDate(params)
        )
      },
    ];

  }

  /*
 * Overlay displayed when no data
 */
  const noRowsOverlay = () => {
    return (
      <GridOverlay>
        <div style={styles.noRows}>No transferable pass requests for the selected date range</div>
      </GridOverlay>
    );
  }

  /*
* Render date column
*/
  const renderDate = (params: GridCellParams) => {
    return (
      typeof params.value === 'string' && params.value !== NULL_DATETIME_STRING
        ? dateStringToLocalDateFormatted(params.value, 'MM/dd/yyyy HH:mm:ss')
        : 'n/a'
    );
  }

  /*
* Render pass date column
*/
  const renderPassDate = (params: GridCellParams) => {
    return (
      typeof params.value === 'string' && params.value !== NULL_DATETIME_STRING
        ? dateStringToLocalDateFormatted(params.value, 'MM/dd/yyyy HH:mm:ss')
        : 'n/a'
    );
  }

  /*
 * Render the request status icon
 */
  const renderStatus = (params: GridCellParams) => {

    const row = params.row;

    switch (row.statusID) {
      case PassStatusID.Requested:
        return (
          <Tooltip title={row.status} arrow>
            <PendingIcon fontSize="medium" sx={styles.pending} />
          </Tooltip>
        )
      case PassStatusID.Confirmed:
        return (
          <Tooltip title={row.status} arrow>
            <CheckCircleIcon fontSize="medium" sx={styles.confirmed} />
          </Tooltip>
        )
      case PassStatusID.Denied:
        return (
          <Tooltip title={row.status} arrow>
            <BlockIcon fontSize="medium" sx={styles.deined} />
          </Tooltip>
        )
      default:
        return (<span />)
    }

  }

  // #endregion

  return (
    <Box sx={styles.root}>
      <DataGrid
        checkboxSelection
        columns={columns()}
        slots={{
          noRowsOverlay: noRowsOverlay
        }}
        density="compact"
        disableColumnSelector
        disableRowSelectionExcludeModel
        disableRowSelectionOnClick
        getRowId={(row) => row.transferablePassID}
        hideFooter
        loading={props.isLoading}
        onRowSelectionModelChange={(r: GridRowSelectionModel) => {
          // Create a new version of the selection model where IDs are forced to numbers
          const numericModel = {
            type: r.type,
            ids: new Set(Array.from(r.ids).map(id => Number(id)))
          };

          // Pass the converted model to your prop handler
          props.handlePassesSelected(numericModel);
        }}
        rows={props.rows}
        rowSelectionModel={props.passesSelected as GridRowSelectionModel}
      />
    </Box>
  );

}

export default PassesListGrid;